import { Injectable } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { StorageLoader } from '@utils/storageLoader';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
}

export type PwaInstallResult =
  | 'accepted'
  | 'dismissed'
  | 'manual'
  | 'unavailable';

@Injectable({ providedIn: 'root' })
export class PwaService {
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private isInstalling = false;
  private isListening = false;
  private updatePromptShown = false;
  private manualInstallPlatform: 'ios' | null = null;

  showInstallButton = false;
  showInstallBanner = false;
  showUpdateBanner = false;

  constructor(
    private storageLoader: StorageLoader,
    private swUpdate: SwUpdate,
  ) {}

  initialize(): void {
    this.refreshInstallState();
    this.listenForInstallPrompt();
    this.listenForAppInstalled();
    void this.queueManualInstallBanner();
    this.checkVersion();
  }

  refreshInstallState(): void {
    const installed = this.isInstalled();

    this.manualInstallPlatform =
      !installed && this.isIosDevice() && !this.deferredPrompt ? 'ios' : null;
    this.showInstallButton = !installed;

    if (!this.showInstallButton) {
      this.showInstallBanner = false;
      this.deferredPrompt = null;
      this.manualInstallPlatform = null;
    }
  }

  get isIosInstall(): boolean {
    return this.manualInstallPlatform === 'ios';
  }

  async install(): Promise<PwaInstallResult> {
    this.refreshInstallState();

    if (this.requiresManualInstall()) {
      this.showInstallBanner = true;
      return 'manual';
    }

    if (!this.deferredPrompt || this.isInstalling) return 'unavailable';

    try {
      this.isInstalling = true;

      await this.deferredPrompt.prompt();

      const result = await this.deferredPrompt.userChoice;
      const accepted = result.outcome === 'accepted';

      if (accepted) {
        this.showInstallBanner = false;
        this.showInstallButton = false;
      }

      return accepted ? 'accepted' : 'dismissed';
    } catch (error) {
      console.error('[Error] PWA install:', error);
      return 'unavailable';
    } finally {
      this.deferredPrompt = null;
      this.isInstalling = false;
    }
  }

  async dismissInstallBanner(): Promise<void> {
    this.showInstallBanner = false;
    await this.storageLoader.setPwaBannerDismissed();
  }

  dismissUpdateBanner(): void {
    this.showUpdateBanner = false;
  }

  async updateApp(): Promise<void> {
    if (!this.swUpdate.isEnabled) return;

    await this.swUpdate.activateUpdate();

    this.showUpdateBanner = false;

    document.location.reload();
  }

  private isInstalled(): boolean {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true
    );
  }

  private requiresManualInstall(): boolean {
    return this.manualInstallPlatform === 'ios';
  }

  private isIosDevice(): boolean {
    const userAgent = window.navigator.userAgent || '';
    const platform = window.navigator.platform || '';
    const maxTouchPoints = window.navigator.maxTouchPoints || 0;

    return (
      /iPad|iPhone|iPod/.test(userAgent) ||
      (platform === 'MacIntel' && maxTouchPoints > 1)
    );
  }

  private async queueManualInstallBanner(): Promise<void> {
    if (!this.requiresManualInstall()) return;

    const dismissed = await this.storageLoader.getPwaBannerDismissed();

    if (dismissed) return;

    setTimeout(() => {
      this.refreshInstallState();

      if (
        this.requiresManualInstall() &&
        this.showInstallButton &&
        !this.deferredPrompt
      ) {
        this.showInstallBanner = true;
      }
    }, 3000);
  }

  private listenForInstallPrompt(): void {
    if (this.isListening) return;

    this.isListening = true;

    window.addEventListener('beforeinstallprompt', async (event: Event) => {
      event.preventDefault();

      this.deferredPrompt = event as BeforeInstallPromptEvent;
      this.refreshInstallState();

      const dismissed = await this.storageLoader.getPwaBannerDismissed();

      if (!dismissed && this.showInstallButton) {
        setTimeout(() => {
          if (this.deferredPrompt && this.showInstallButton) {
            this.showInstallBanner = true;
          }
        }, 3000);
      }
    });
  }

  private listenForAppInstalled(): void {
    window.addEventListener('appinstalled', () => {
      this.showInstallButton = false;
      this.showInstallBanner = false;
      this.deferredPrompt = null;
    });
  }

  private async checkVersion(): Promise<void> {
    try {
      if (!this.swUpdate.isEnabled) {
        console.warn('[Disabled] Service Worker');
        return;
      }

      this.swUpdate.versionUpdates.subscribe((event) => {
        if (event.type === 'VERSION_READY' && !this.updatePromptShown) {
          this.updatePromptShown = true;
          this.showUpdateBanner = true;
        }
      });

      await this.swUpdate.checkForUpdate();
    } catch (error) {
      console.error('[Error] Service Worker:', error);
    }
  }
}
